import React, {useEffect, useState, useCallback} from 'react';
import {View, ScrollView, StyleSheet, TouchableOpacity} from 'react-native';
import {TextInput, Button, Snackbar, Text, Switch} from 'react-native-paper';
import {useTranslation} from 'react-i18next';
import {useNavigation} from '@react-navigation/native';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useConfigStore} from '../../store/useConfigStore';
import GradientHeader from '../../components/common/GradientHeader';
import {colors, spacing} from '../../theme';

const TaxSettingsScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const {fetchByType, getProperty, updateProperties, isLoading} = useConfigStore();

  const [taxEnabled, setTaxEnabled] = useState(false);
  const [taxName, setTaxName] = useState('');
  const [taxNumber, setTaxNumber] = useState('');
  const [taxLines, setTaxLines] = useState([]);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState('');

  useEffect(() => {
    fetchByType('TAX');
  }, [fetchByType]);

  useEffect(() => {
    setTaxEnabled(getProperty('TAX', 'TAX_ENABLED') === 'true');
    setTaxName(getProperty('TAX', 'TAX_NAME'));
    setTaxNumber(getProperty('TAX', 'TAX_NUMBER'));
    const linesJson = getProperty('TAX', 'TAX_LINES');
    if (linesJson) {
      try {
        setTaxLines(JSON.parse(linesJson));
      } catch (_) {}
    }
  }, [getProperty, useConfigStore.getState().properties]);

  const addTaxLine = useCallback(() => {
    setTaxLines(prev => [...prev, {name: '', percentage: ''}]);
  }, []);

  const updateTaxLine = useCallback((index, field, value) => {
    setTaxLines(prev => prev.map((line, i) => i === index ? {...line, [field]: value} : line));
  }, []);

  const removeTaxLine = useCallback(index => {
    setTaxLines(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleSave = useCallback(async () => {
    setSaving(true);
    try {
      const cleanLines = taxLines
        .filter(l => l.name && l.percentage)
        .map(l => ({name: l.name, percentage: Number(l.percentage)}));

      await updateProperties('TAX', [
        {code: 'TAX_ENABLED', name: 'Tax Enabled', value: String(taxEnabled)},
        {code: 'TAX_NAME', name: 'Tax Name', value: taxName},
        {code: 'TAX_NUMBER', name: 'Tax Number', value: taxNumber},
        {code: 'TAX_LINES', name: 'Tax Lines', value: JSON.stringify(cleanLines)},
      ]);
      setSnackbar(t('config.saved'));
    } catch (e) {
      setSnackbar(e.response?.data?.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  }, [taxEnabled, taxName, taxNumber, taxLines, updateProperties, t]);

  return (
    <View style={styles.container}>
      <GradientHeader title={t('config.taxSettings')} onBack={() => navigation.goBack()} />

      <ScrollView contentContainerStyle={styles.scrollContent}>
        {/* Enable/Disable */}
        <View style={styles.section}>
          <View style={styles.switchRow}>
            <View style={styles.switchLeft}>
              <Icon name="percent-outline" size={18} color={colors.primary} />
              <View>
                <Text style={styles.switchLabel}>{t('config.enableTax')}</Text>
                <Text style={styles.switchHint}>Apply tax on customer bills</Text>
              </View>
            </View>
            <Switch value={taxEnabled} onValueChange={setTaxEnabled} color={colors.primary} />
          </View>
        </View>

        {taxEnabled && (
          <>
            {/* Tax Identity */}
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Icon name="card-account-details-outline" size={18} color={colors.primary} />
                <Text style={styles.sectionTitle}>Tax Identity</Text>
              </View>
              <Text style={styles.sectionHint}>Your business tax registration details</Text>

              <TextInput
                label={t('config.taxName')}
                value={taxName}
                onChangeText={setTaxName}
                mode="outlined"
                placeholder="e.g. GST"
                style={styles.input}
                outlineColor={colors.border}
                activeOutlineColor={colors.primary}
              />
              <TextInput
                label={t('config.taxNumber')}
                value={taxNumber}
                onChangeText={setTaxNumber}
                mode="outlined"
                placeholder="e.g. 29AABCU9603R1ZM"
                style={styles.input}
                outlineColor={colors.border}
                activeOutlineColor={colors.primary}
              />
            </View>

            {/* Tax Lines */}
            <View style={styles.section}>
              <View style={styles.sectionHeader}>
                <Icon name="format-list-numbered" size={18} color={colors.primary} />
                <Text style={styles.sectionTitle}>Tax Breakdown</Text>
              </View>
              <Text style={styles.sectionHint}>Add individual tax components (e.g. CGST 9%, SGST 9%)</Text>

              {taxLines.map((line, index) => (
                <View key={index} style={styles.taxLineRow}>
                  <TextInput
                    mode="outlined"
                    placeholder="Name (e.g. CGST)"
                    value={line.name}
                    onChangeText={v => updateTaxLine(index, 'name', v)}
                    style={[styles.input, styles.taxNameInput]}
                    outlineColor={colors.border}
                    activeOutlineColor={colors.primary}
                    dense
                  />
                  <TextInput
                    mode="outlined"
                    placeholder="%"
                    value={String(line.percentage)}
                    onChangeText={v => updateTaxLine(index, 'percentage', v)}
                    keyboardType="numeric"
                    style={[styles.input, styles.taxPercentInput]}
                    outlineColor={colors.border}
                    activeOutlineColor={colors.primary}
                    dense
                  />
                  <TouchableOpacity
                    onPress={() => removeTaxLine(index)}
                    style={styles.removeTaxBtn}
                    hitSlop={{top: 8, bottom: 8, left: 8, right: 8}}>
                    <Icon name="close-circle" size={22} color={colors.error} />
                  </TouchableOpacity>
                </View>
              ))}

              <TouchableOpacity style={styles.addTaxRow} onPress={addTaxLine} activeOpacity={0.7}>
                <Icon name="plus-circle-outline" size={18} color={colors.primary} />
                <Text style={styles.addTaxText}>{t('config.addTax')}</Text>
              </TouchableOpacity>
            </View>
          </>
        )}

        <Button
          mode="contained"
          onPress={handleSave}
          loading={saving}
          disabled={saving || isLoading}
          style={styles.saveBtn}
          buttonColor={colors.primary}>
          {t('common.save')}
        </Button>
      </ScrollView>

      <Snackbar visible={!!snackbar} onDismiss={() => setSnackbar('')} duration={2000}>
        {snackbar}
      </Snackbar>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  scrollContent: {paddingBottom: spacing.xl},

  section: {
    backgroundColor: colors.surface,
    marginBottom: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginBottom: 2,
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '700',
    color: colors.text,
  },
  sectionHint: {
    fontSize: 12,
    color: colors.textSecondary,
    marginBottom: spacing.md,
    marginLeft: 24,
  },
  input: {marginBottom: spacing.sm, backgroundColor: colors.surface},
  saveBtn: {marginHorizontal: spacing.md, marginTop: spacing.sm, borderRadius: 8},

  // Switch
  switchRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  switchLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    flex: 1,
  },
  switchLabel: {
    fontSize: 15,
    fontWeight: '600',
    color: colors.text,
  },
  switchHint: {
    fontSize: 12,
    color: colors.textSecondary,
    marginTop: 1,
  },

  // Tax lines
  taxLineRow: {flexDirection: 'row', alignItems: 'center', gap: spacing.xs},
  taxNameInput: {flex: 2},
  taxPercentInput: {flex: 1},
  removeTaxBtn: {marginBottom: spacing.sm},
  addTaxRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingVertical: spacing.xs,
  },
  addTaxText: {
    fontSize: 14,
    fontWeight: '600',
    color: colors.primary,
  },
});

export default TaxSettingsScreen;
